import { arcgisToGeoJSON } from "@esri/arcgis-to-geojson-utils";
import { loadModules } from "esri-loader";
import moment from "moment";
import { get, writable } from "svelte/store";
import LoadState from "../Enums/LoadState";
import GeoHelper from "../Helpers/GeoHelper";
import MapLayerGroup from "../Map/Enums/MapLayerGroup";
import VisibilityState from "../Map/Enums/VisibilityState";
import MapLayerManager from "../Map/MapLayerManager";
import { Paint } from "../Map/MapLayerStyles";
import PopupStore from "../Map/Popup/PopupStore";
import MapStore from "../Map/Store";
import Vessel from "../Models/Vessel";

import { adjustTimezoneToUTC, canAdjustTimezoneToUTC } from "../Helpers/Misc/timeHelper";

import HurricaneDataLayer from "./HurricaneDataLayer";

const initLayerData = () => {
	return {
		layers: [],
		visible: writable(true)
	};
};
const HurricanesStore = {
	config: {
		token: "",
		server: "https://services9.arcgis.com/RHVPKKiFTONKtxq3/arcgis/rest/services"
	},
	meta: {
		loadState: writable(LoadState.INIT),
		layers: [],
		// Not sure what to call this. A map of HurricaneDataLayer to mapbox layers.
		layerData: {
			[HurricaneDataLayer.FORECAST_POSITION]: initLayerData(),
			[HurricaneDataLayer.FORECAST_ERRORCONE]: initLayerData(),
			[HurricaneDataLayer.HISTORIC_POSITION]: initLayerData(),
			[HurricaneDataLayer.FORCE_34KNOTS]: initLayerData(),
			[HurricaneDataLayer.FORCE_50KNOTS]: initLayerData(),
			[HurricaneDataLayer.FORCE_64PLUSKNOTS]: initLayerData()
		},
		opacity: writable(1.0)
	},
	actions: {
		init: () => {
			// Already initted
			if (get(HurricanesStore.meta.loadState) !== LoadState.INIT) {
				return;
			}

			HurricanesStore.meta.loadState.set(LoadState.LOADING);
			console.log("Loading Hurricanes...");

			const addDataLayer = (name, type, paint, featureCollection) => {
				const map = MapStore.helpers.getMap();
				map.addSource(name, {
					type: "geojson",
					data: featureCollection
				});
				map.addLayer(
					{
						id: name,
						type: type,
						source: name,
						paint: paint
					},
					"waterway-label" // MapLayer.VESSELS_NEARBY_LINES
				);
				HurricanesStore.meta.layers.push(name);
			};

			const token = HurricanesStore.config.token;
			const server = HurricanesStore.config.server;

			loadModules(["esri/Map", "esri/layers/FeatureLayer", "esri/identity/IdentityManager", "esri/tasks/support/Query"])
				.then((classes) => {
					const FeatureLayer = classes[1];
					const IdentityManager = classes[2];

					if (token.length > 0) {
						IdentityManager.registerToken({
							token: token,
							server: server
						});
					}

					const hurricanesUrl = server + "/Active_Hurricanes_v1/FeatureServer";

					const hurricanesForecastLayer = new FeatureLayer({ url: hurricanesUrl, layerId: 0 });
					const hurricanesObservedLayer = new FeatureLayer({ url: hurricanesUrl, layerId: 1 });
					const hurricanesForecastLineLayer = new FeatureLayer({ url: hurricanesUrl, layerId: 2 });
					const hurricanesObservedLineLayer = new FeatureLayer({ url: hurricanesUrl, layerId: 3 });
					const hurricanesConeLayer = new FeatureLayer({ url: hurricanesUrl, layerId: 4 });
					const hurricanes34kts = new FeatureLayer({ url: hurricanesUrl, layerId: 7 });
					const hurricanes50kts = new FeatureLayer({ url: hurricanesUrl, layerId: 8 });
					const hurricanes64kts = new FeatureLayer({ url: hurricanesUrl, layerId: 9 });

					Promise.all([
						hurricanesForecastLayer.queryFeatures(),
						hurricanesObservedLayer.queryFeatures(),
						hurricanesForecastLineLayer.queryFeatures(),
						hurricanesObservedLineLayer.queryFeatures(),
						hurricanesConeLayer.queryFeatures(),
						hurricanes34kts.queryFeatures(),
						hurricanes50kts.queryFeatures(),
						hurricanes64kts.queryFeatures()
					]).then((responses) => {
						console.log("Loaded Hurricanes...", responses);
						const map = MapStore.helpers.getMap();

						// Severity polygons - 7,6,5
						const stormColorLayers = [responses[7], responses[6], responses[5]];
						for (let j = 0; j < stormColorLayers.length; j++) {
							const stormsFeatureCollection = GeoHelper.emptyGeoJson();
							const data = stormColorLayers[j];
							for (let i = 0; i < data.features.length; i++) {
								const feature = arcgisToGeoJSON(data.features[i]);
								stormsFeatureCollection.features.push(feature);
							}
							addDataLayer("hurricane_storms_" + j, "fill", Paint.hurricanes.probability, stormsFeatureCollection);
						}
						HurricanesStore.meta.layerData[HurricaneDataLayer.FORCE_64PLUSKNOTS].layers.push("hurricane_storms_0");
						HurricanesStore.meta.layerData[HurricaneDataLayer.FORCE_50KNOTS].layers.push("hurricane_storms_1");
						HurricanesStore.meta.layerData[HurricaneDataLayer.FORCE_34KNOTS].layers.push("hurricane_storms_2");

						// Points?
						const pointLayers = [responses[0], responses[1]];
						for (let j = 0; j < pointLayers.length; j++) {
							const pointsFeatureCollection = GeoHelper.emptyGeoJson();
							const data = pointLayers[j];
							for (let i = 0; i < data.features.length; i++) {
								const feature = arcgisToGeoJSON(data.features[i]);
								pointsFeatureCollection.features.push(feature);
							}
							addDataLayer("hurricane_points_" + j, "circle", Paint.hurricanes.points, pointsFeatureCollection);
						}
						HurricanesStore.meta.layerData[HurricaneDataLayer.FORECAST_POSITION].layers.push("hurricane_points_0");
						HurricanesStore.meta.layerData[HurricaneDataLayer.HISTORIC_POSITION].layers.push("hurricane_points_1");

						// console.log("hurricanes", responses[0], responses[1]);
						// console.log("hurricanes lowest force", responses[5]);

						// Lines?
						const lineLayers = [responses[2], responses[3]];
						for (let j = 0; j < lineLayers.length; j++) {
							const linesFeatureCollection = GeoHelper.emptyGeoJson();
							const data = lineLayers[j];
							for (let i = 0; i < data.features.length; i++) {
								const feature = arcgisToGeoJSON(data.features[i]);
								linesFeatureCollection.features.push(feature);
							}
							addDataLayer("hurricane_lines_" + j, "line", Paint.hurricanes.lines, linesFeatureCollection);
						}
						HurricanesStore.meta.layerData[HurricaneDataLayer.FORECAST_POSITION].layers.push("hurricane_lines_0");
						HurricanesStore.meta.layerData[HurricaneDataLayer.HISTORIC_POSITION].layers.push("hurricane_lines_1");

						// Polygon
						const coneFeatureCollection = GeoHelper.emptyGeoJson();
						const hurricanePolygonData = responses[4];
						for (let i = 0; i < hurricanePolygonData.features.length; i++) {
							const feature = arcgisToGeoJSON(hurricanePolygonData.features[i]);
							coneFeatureCollection.features.push(feature);
						}
						addDataLayer("hurricane_cone", "fill", Paint.hurricanes.cone, coneFeatureCollection);
						HurricanesStore.meta.layerData[HurricaneDataLayer.FORECAST_ERRORCONE].layers.push("hurricane_cone");

						// let onEnterCone = (e) => {}
						// let onLeaveCone = () => {};
						// map.on("mouseenter", HurricaneDataLayer.FORECAST_ERRORCONE, onEnterCone);
						// map.on("mousemove", HurricaneDataLayer.FORECAST_ERRORCONE, onEnterCone);
						// map.on("mouseleave", HurricaneDataLayer.FORECAST_ERRORCONE, onLeaveCone);

						const onEnterPoint = (e) => {
							if (e.features.length > 0) {
								const rawTime = e.features[0].properties.FLDATELBL.substr(0, 19);
								let forecastTime = moment(rawTime, "YYYY-MM-DD hh:mm A", true);

								// add offset to the time based on timezone inthe string.
								const timezone = e.features[0].properties.TIMEZONE;
								const coords = e.features[0].geometry.coordinates;
								let displayTime = rawTime;
								if (canAdjustTimezoneToUTC(forecastTime, timezone, coords)) {
									forecastTime = adjustTimezoneToUTC(forecastTime, timezone, coords);
									displayTime = forecastTime.format("MMMM Do YYYY, hh:mm:ssa [(UTC)]");
								}

								// console.log(e.features[0]);
								const props = {
									Name: e.features[0].properties.STORMNAME,
									Type: e.features[0].properties.TCDVLP,
									"Forecast Max Wind Speed": Vessel.formatKnots(e.features[0].properties.MAXWIND),
									"Forecast Time": displayTime
								};
								PopupStore.actions.custom(props, e.features[0].geometry.coordinates, true);
							}
						};
						const onLeavePoint = () => {
							PopupStore.actions.custom({}, null, false);
						};
						map.on("mouseenter", HurricanesStore.meta.layerData[HurricaneDataLayer.FORECAST_POSITION].layers[0], onEnterPoint);
						map.on("mouseleave", HurricanesStore.meta.layerData[HurricaneDataLayer.FORECAST_POSITION].layers[0], onLeavePoint);

						const onEnterHistoricPoint = (e) => {
							if (e.features.length > 0) {
								// console.log(e.features[0]);
								const props = {
									Name: e.features[0].properties.STORMNAME,
									Type: e.features[0].properties.STORMTYPE,
									Time: moment(new Date(e.features[0].properties.DTG)).format("MMMM Do YYYY, hh:mm:ssa [(UTC)]")
								};
								PopupStore.actions.custom(props, e.features[0].geometry.coordinates, true);
							}
						};
						map.on("mouseenter", HurricanesStore.meta.layerData[HurricaneDataLayer.HISTORIC_POSITION].layers[0], onEnterHistoricPoint);
						map.on("mouseleave", HurricanesStore.meta.layerData[HurricaneDataLayer.HISTORIC_POSITION].layers[0], onLeavePoint);

						MapLayerManager.addLayerGroup(MapLayerGroup.TROPICAL_STORMS, HurricanesStore.meta.layers, HurricanesStore.meta.layers);

						HurricanesStore.meta.loadState.set(LoadState.LOADED);
					});
				});
		},
		updateVisibility: () => {
			const opacity = get(HurricanesStore.meta.opacity);
			const map = MapStore.helpers.getMap();
			if (map) {
				if (get(HurricanesStore.meta.loadState) !== LoadState.LOADED) {
					return;
				}
				const visible = get(MapStore.hurricanesVisible);
				const dataLayers = Object.keys(HurricanesStore.meta.layerData);
				for (let i = 0; i < dataLayers.length; i++) {
					const layer = HurricanesStore.meta.layerData[dataLayers[i]];
					for (let j = 0; j < layer.layers.length; j++) {
						const layerVisible = get(layer.visible);
						map.setLayoutProperty(layer.layers[j], "visibility", (visible && layerVisible) ? VisibilityState.VISIBLE : VisibilityState.NONE);
						const layerData = map.getLayer(layer.layers[j]);
						// console.log("layerData", layerData);
						if (layerData.type === "fill") {
							map.setPaintProperty(layer.layers[j], "fill-opacity", opacity);
						}
						else if (layerData.type === "circle") {
							map.setPaintProperty(layer.layers[j], "circle-opacity", opacity);
						}
						else if (layerData.type === "line") {
							map.setPaintProperty(layer.layers[j], "line-opacity", opacity);
						}
					}
				}

				map.setPaintProperty("hurricane_cone", "fill-opacity", opacity * 0.2);
			}
		}
	}
};

export default HurricanesStore;
